import { useEffect } from "react";
import { css } from "@emotion/react";
import { Box, FormControl, useTheme } from "@mui/material";
import { Element } from "react-scroll";

import type {
  DropdownFormBuilderField,
  MhFormContext,
} from "@thalamos/aspire-common";

import type { FieldProps } from "../../pages/FieldProps.js";
import { Dropdown } from "../design-system/Dropdown/Dropdown.js";
import { FormLabel } from "../design-system/FormLabel.js";
import { HelperText } from "../design-system/HelperText/HelperText.js";

export type DropdownFormFieldProps<Data extends { [k: string]: any }> = {
  field: DropdownFormBuilderField<Data>;
  fieldProps: FieldProps<Data>;
};

export function DropdownFormField<Data extends { [k: string]: any }>({
  field,
  fieldProps,
}: DropdownFormFieldProps<Data>) {
  const isMulti = field.isMultiSelect;
  const selectedValue = isMulti
    ? (fieldProps.values[field.valueField] ?? [])
    : fieldProps.values[field.valueField];
  const fieldTouched = fieldProps.touched[field.valueField];
  const isError = !!(fieldTouched && fieldProps.errors[field.valueField]);
  const isReadOnly = field.isReadOnlyFn?.(
    fieldProps.values,
    fieldProps.context as MhFormContext,
  );

  const theme = useTheme();

  // A field's default is not necessarily derivable on first render: it may
  // read parts of the form context that resolve later. It is therefore derived
  // on every render and the effect keys on the derived value itself — the
  // context's identity is not render-stable, so it cannot be a dependency. A
  // field the user has already touched keeps their answer: not every default
  // is read-only once it resolves, so a derived value must not overwrite a
  // deliberate choice.
  const defaultVal =
    !fieldTouched && field.defaultFn
      ? field.defaultFn(fieldProps.values, fieldProps.context as MhFormContext)
      : undefined;
  const isEmpty =
    selectedValue === null ||
    selectedValue === undefined ||
    selectedValue === "" ||
    (isMulti && Array.isArray(selectedValue) && selectedValue.length === 0);

  useEffect(() => {
    if (!isEmpty || defaultVal === undefined) return;

    fieldProps.setValues({
      ...fieldProps.values,
      [field.valueField]: isMulti ? [defaultVal.value] : defaultVal.value,
      [field.textField]: isMulti ? [defaultVal.label] : defaultVal.label,
    });
  }, [isEmpty, defaultVal?.value, defaultVal?.label]);

  return (
    <Element name={field.valueField} data-testid={`field:${field.textField}`}>
      <FormControl
        css={css`
          width: 100%;
          margin-bottom: ${theme.spacing(1)};
        `}
      >
        {field.label && <FormLabel label={field.label} error={isError} />}

        <Dropdown
          name={field.valueField}
          values={field.options}
          placeholder={field.placeholder}
          readOnly={isReadOnly}
          multiple={isMulti}
          selectedValue={selectedValue}
          onChange={(z) => {
            let newValues: string | string[];
            let newLabels;

            if (isMulti) {
              newValues = z;
              newLabels = field.options
                .filter((o) => newValues.includes(o.value))
                .map((o) => o.label);
            } else {
              const selected = field.options.find((o) => o.value === z);
              newValues = selected?.value ?? "";
              newLabels = selected?.label ?? "";
            }

            fieldProps.setFieldTouched(field.valueField, true, false);
            fieldProps.setValues({
              ...fieldProps.values,
              [field.valueField]: newValues,
              [field.textField]: newLabels,
              ...Object.fromEntries(
                field.fieldsOwned?.map((field) => [
                  field,
                  fieldProps.validationSchema.getDefault()[field],
                ]) || [],
              ),
            });
          }}
        />
        {field.afterLabel && <HelperText subtext={field.afterLabel} />}
        {isError && (
          <Box sx={{ mb: 2 }}>
            <HelperText
              errorMessage={fieldProps.errors[field.valueField] as string}
            />
          </Box>
        )}
      </FormControl>
    </Element>
  );
}
